/**
 * InstallAppButton — surfaces a "Download / Install app" CTA that's
 * always visible (unlike the auto-popup PwaInstallBanner). Used on the
 * LoginPage so prospects can install before signing in.
 *
 * Behavior:
 *   • Already installed (`isStandalone`) → render nothing.
 *   • Chrome/Edge/Android (`canInstall`) → "Install" button → triggers native dialog.
 *   • iOS Safari (`isiOS` AND not canInstall) → "Add to home screen" button → opens an instruction popover.
 *   • Other desktop browsers (no canInstall, not iOS) → render nothing — install isn't possible.
 *
 * Wired via `usePwaInstall()` hook so the singleton `beforeinstallprompt`
 * event is shared between this button and the existing PwaInstallBanner.
 */
import { useState } from "react";
import { Download, DeviceMobile, Share, Plus, X } from "@phosphor-icons/react";
import usePwaInstall from "@/lib/usePwaInstall";
import { useT } from "@/lib/i18n";

export default function InstallAppButton({ variant = "outline", className = "" }) {
  const t = useT();
  const { canInstall, isiOS, isStandalone, install } = usePwaInstall();
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [busy, setBusy] = useState(false);

  if (isStandalone) return null;
  // Hide on desktop browsers that don't support install (Firefox desktop,
  // Safari macOS) — there's nothing to do.
  if (!canInstall && !isiOS) return null;

  const baseClass =
    variant === "primary"
      ? "px-4 py-3 rounded-sm bg-emerald-500 text-black hover:bg-emerald-400 font-bold text-sm uppercase tracking-wider"
      : "px-4 py-2.5 rounded-sm border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 hover:text-blue-700 font-semibold text-sm";

  const onClick = async () => {
    if (canInstall) {
      setBusy(true);
      try {
        await install();
      } finally {
        setBusy(false);
      }
      return;
    }
    if (isiOS) setShowIosHelp(true);
  };

  return (
    <>
      <button
        type="button"
        data-testid="install-app-button"
        onClick={onClick}
        disabled={busy}
        className={`w-full flex items-center justify-center gap-2 transition-colors disabled:opacity-50 ${baseClass} ${className}`}
        title={t("install_btn.tooltip", "Install this app on your device")}
      >
        {isiOS && !canInstall
          ? <DeviceMobile size={16} weight="bold" />
          : <Download size={16} weight="bold" />}
        <span>
          {busy
            ? t("install_btn.installing", "Installing…")
            : isiOS && !canInstall
              ? t("install_btn.add_to_home", "Add to Home Screen")
              : t("install_btn.download", "Download the app")}
        </span>
      </button>

      {showIosHelp && (
        <div
          data-testid="install-app-ios-popover"
          className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIosHelp(false)}
        >
          <div
            className="w-full max-w-sm bg-white rounded-sm shadow-xl border border-slate-200 p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-base font-black tracking-tight font-['Chivo'] flex items-center gap-2">
                <DeviceMobile size={18} weight="fill" className="text-blue-700" />
                {t("install_btn.ios_title", "Install on your iPhone")}
              </h3>
              <button
                onClick={() => setShowIosHelp(false)}
                className="text-slate-400 hover:text-slate-700"
                data-testid="install-app-ios-close"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              {t("install_btn.ios_intro", "iOS doesn't support one-click install — but it takes 3 seconds:")}
            </p>
            <ol className="space-y-2.5 text-sm">
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center shrink-0">1</span>
                <span>
                  {t("install_btn.ios_step1", "Tap the")}
                  <Share size={14} weight="bold" className="inline mx-1.5 align-text-bottom text-blue-700" />
                  {t("install_btn.ios_step1_end", "Share button in Safari's toolbar.")}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center shrink-0">2</span>
                <span>
                  {t("install_btn.ios_step2", "Scroll and tap")}
                  <Plus size={14} weight="bold" className="inline mx-1.5 align-text-bottom text-blue-700" />
                  <strong>{t("install_btn.ios_step2_end", "\"Add to Home Screen\".")}</strong>
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center shrink-0">3</span>
                <span>{t("install_btn.ios_step3", "Confirm — the icon lands on your home screen and opens full-screen.")}</span>
              </li>
            </ol>
            <p className="text-[11px] text-slate-400 mt-4">
              {t("install_btn.ios_safari_only", "Only Safari supports this on iPhone — Chrome / Firefox on iOS use Apple's WebKit and have the same flow.")}
            </p>
          </div>
        </div>
      )}
    </>
  );
}
